/**
 * Отбор строк под удаление пачкой (#2250).
 *
 * ЗАЧЕМ В ЯДРЕ, А НЕ В ПАНЕЛЯХ. Носителей два — панель Studio и панель кабинета, общего
 * UI-пакета у них нет. Две копии одного правила разъезжаются молча, и мы это уже ловили на
 * переносе (30.08) и на согласии показа с действием (31.08). Здесь разъезжаться нечему:
 * решения принимает ядро, панели рисуют.
 *
 * ОСОБЕННО — СЛОВА. `SELECT_ALL_SHOWN_LABEL` живёт константой, потому что формулировка здесь
 * не украшение, а защита: «выбрать всё» и «выбрать всё показанное» — разные обещания, и
 * первое лжёт. Человек думает про набор, действие идёт по странице — класс, записанный в
 * `docs/field/decisions-on-partial-data.md`. Строка в двух домах порознь однажды разойдётся
 * на одно слово, и разойдётся именно на этом.
 */

/**
 * Подпись флажка «выбрать всё показанное».
 *
 * НЕ «выбрать всё». Отбор чарт-листа показывает ВЫБОРКУ из набора, а не набор: за её краем
 * лежат записи, которых человек не видел. Обещать «всё» значило бы обещать то, чего действие
 * не делает.
 */
export const SELECT_ALL_SHOWN_LABEL = 'Выбрать всё показанное';

/**
 * Подпись кнопки удаления пачкой. Число В ПОДПИСИ, а не только в окне: масштаб обязан быть
 * виден ДО нажатия. Окно скажет его ещё раз — это не дублирование, а две разные точки во
 * времени: до решения и в момент подтверждения.
 */
export function bulkDeleteLabel(count: number): string {
  return `Удалить выбранные (${count})`;
}

/** Переключить одну строку. Возвращает НОВОЕ множество — состояние панели иммутабельно. */
export function togglePick(picked: ReadonlySet<string>, id: string): Set<string> {
  const next = new Set(picked);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  return next;
}

/**
 * Все ли ПОКАЗАННЫЕ строки отмечены.
 *
 * Пустой показ — не «всё отмечено»: иначе флажок «выбрать всё показанное» стоял бы взведённым
 * над пустой таблицей и предлагал удалить ничто.
 */
export function allShownPicked(picked: ReadonlySet<string>, shownIds: readonly string[]): boolean {
  if (shownIds.length === 0) return false;
  return shownIds.every((id) => picked.has(id));
}

/**
 * Переключение «всё показанное»: отмечено всё — снять показанные, иначе добавить показанные.
 *
 * Отметки за пределами показа СОХРАНЯЮТСЯ. Человек мог отобрать строки, сменить окно дат и
 * вернуться; молча стереть его прежний выбор значило бы потерять работу без слова. Снятие
 * тоже трогает только показанное — по той же причине.
 */
export function toggleAllShown(picked: ReadonlySet<string>, shownIds: readonly string[]): Set<string> {
  const next = new Set(picked);
  if (allShownPicked(picked, shownIds)) {
    for (const id of shownIds) next.delete(id);
    return next;
  }
  for (const id of shownIds) next.add(id);
  return next;
}

/**
 * Что реально уйдёт: отмеченное, СУЖЕННОЕ до показанного, в порядке показа.
 *
 * Сужение обязательно. Отметка могла пережить смену окна отбора, и тогда в списке лежит id,
 * которого человек сейчас не видит: удалить его значило бы удалить невидимое. Порядок берётся
 * от показа, чтобы список в окне подтверждения читался так же, как таблица.
 */
export function pickedShownIds(picked: ReadonlySet<string>, shownIds: readonly string[]): string[] {
  return shownIds.filter((id) => picked.has(id));
}

/** Снять отметки с ушедших записей — после удаления они не должны «остаться выбранными». */
export function forgetPicks(picked: ReadonlySet<string>, goneIds: readonly string[]): Set<string> {
  const next = new Set(picked);
  for (const id of goneIds) next.delete(id);
  return next;
}
